import { DrawerProps, IconButton, Drawer as MuiDrawer } from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayPortalProvider } from '../overlay-portal';
import { cn } from '../utils';

function DrawerOverlay({
  children,
  paperSx,
  className,
  ...props
}: DrawerProps & { paperSx: DrawerProps['sx'] }) {
  const [paper, setPaper] = useState<HTMLElement | null>(null);

  return (
    <MuiDrawer
      {...props}
      className={className}
      onContextMenu={(e) => {
        // The drawer portals out of its React ancestors' DOM subtree, but
        // events still bubble to them through the portal. Stop right-clicks
        // from reaching those ancestors (e.g. the block sheet's context menu),
        // which would suppress the native context menu and steal field focus.
        e.stopPropagation();
        props.onContextMenu?.(e);
      }}
      PaperProps={{
        ...props.PaperProps,
        ref: setPaper,
        'data-overlay-portal-root': '',
      }}
      sx={paperSx}
    >
      <OverlayPortalProvider container={paper}>
        {children}
      </OverlayPortalProvider>
    </MuiDrawer>
  );
}

export function TabletDrawer({ children, ...props }: DrawerProps) {
  return (
    <DrawerOverlay
      {...props}
      anchor="right"
      className="h-dvh"
      paperSx={{
        '& .MuiDrawer-paper': {
          width: '100%',
          overflowY: 'hidden',
          overflowX: 'hidden',
        },
      }}
    >
      {children}
    </DrawerOverlay>
  );
}

export function Drawer({ children, ...props }: DrawerProps) {
  const childArray = React.Children.toArray(children);

  const header =
    childArray.find(
      (c) => React.isValidElement(c) && c.type === DrawerHeader,
    ) ?? null;

  const footer =
    childArray.find(
      (c) => React.isValidElement(c) && c.type === DrawerFooter,
    ) ?? null;

  // Split out the actual content so we can add scrolling to it
  const content = childArray.filter(
    (child) =>
      React.isValidElement(child) && child !== header && child !== footer,
  );

  return (
    <DrawerOverlay
      {...props}
      anchor="right"
      paperSx={{
        '& .MuiDrawer-paper': {
          width: 460,
          minWidth: 460,
          height: '100dvh',
          maxHeight: '100dvh',
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {header}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {content}
      </div>
      {footer}
    </DrawerOverlay>
  );
}

type DrawerHeaderProps = {
  children?: React.ReactNode;
  title?: string;
  className?: string;
  onClose?: () => void;
};

export function DrawerHeader({
  title,
  children,
  className,
  onClose,
  ...props
}: DrawerHeaderProps) {
  const { t } = useTranslation();
  return (
    <div
      {...props}
      className={cn('flex row justify-between items-center p-6', className)}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="w-full">
        {title && <h2 className="m-0 text-xl font-semibold">{title}</h2>}
        {children}
      </div>
      {onClose && (
        <div className="h-full">
          <IconButton
            aria-label={t('close')}
            onClick={onClose}
            sx={{ padding: 0, width: '30px', height: '30px' }}
          >
            <MdClose size={24} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

type DrawerFooterProps = {
  className?: string;
  children: React.ReactNode;
};

/**
 * DrawerFooter component for use with Drawer.
 *
 * IMPORTANT: DrawerFooter must be a direct child of Drawer (not nested inside Form/form elements).
 * The Drawer component extracts DrawerFooter and positions it at the bottom automatically.
 *
 * If you need a submit button in the footer for a form inside the drawer:
 * 1. Add an id to your form: <form id="my-form">
 * 2. Place DrawerFooter outside the form as a direct child of Drawer
 * 3. Link the submit button to the form: <Button type="submit" form="my-form">
 *
 * @example
 * <Drawer>
 *   <DrawerHeader />
 *   <form id="my-form">...</form>
 *   <DrawerFooter>
 *     <Button type="submit" form="my-form">Save</Button>
 *   </DrawerFooter>
 * </Drawer>
 */
export function DrawerFooter({ className, children }: DrawerFooterProps) {
  return (
    <div
      className={cn('border-t-[1px] border-gray-300 w-full pt-2', className)}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}
